import { ReactElement, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import FormCard from '../../components/FormCard';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Table from '../../components/Table';
import { useEpisodeList } from '../../hooks/useEpisodeDbCache';
import { useTeamEpisodeFiles } from '../../hooks/useEpisodeDbScan';
import { useUser } from '../../hooks/useUser';
import { cn } from '../../utils/cn';
import { isAdmin } from '../../utils/userRole';

/**
 * Account → Recordings: the Nimbus recording files (.db) a phone has
 * uploaded. Everyone sees their own team's: files of a phone the team records
 * with, and files a team login uploaded (the server stamps the uploader on
 * each file since 2026-10-16). An admin can also list every
 * team's — the reach the admin episode viewer had before it moved here —
 * and that list alone knows which files are already in the server's cache.
 */
interface RecordingRow {
  s3_key: string;
  filename: string;
  captured_ts: string;
  device_id: string | null;
  // Only the all-teams list reports it; undefined on the team list.
  cached?: boolean;
}

// How far back from the chosen date the list reaches. `null` start = all.
type SpanKey = 'week' | 'month' | 'year' | 'all';

const SPANS: { key: SpanKey; label: string }[] = [
  { key: 'week', label: '1 week' },
  { key: 'month', label: '1 month' },
  { key: 'year', label: '1 year' },
  { key: 'all', label: 'All' },
];

// The default span is the widest one that lists fewer than this many files.
const DEFAULT_SPAN_MAX_ROWS = 15;

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The end of the chosen day, in UTC like the capture times the list shows. */
function endOfDay(date: string): Date {
  return new Date(`${date}T23:59:59Z`);
}

/** Where a span starts, counting back from `end`; null for "all". */
function spanStart(span: SpanKey, end: Date): Date | null {
  const start = new Date(end);
  if (span === 'week') start.setUTCDate(start.getUTCDate() - 7);
  else if (span === 'month') start.setUTCMonth(start.getUTCMonth() - 1);
  else if (span === 'year') start.setUTCFullYear(start.getUTCFullYear() - 1);
  else return null;
  return start;
}

/** The rows captured within *span* of *end*: all of them for the open-ended span. */
function rowsInSpan(rows: RecordingRow[], span: SpanKey, end: Date): RecordingRow[] {
  const start = spanStart(span, end);
  return start ? rows.filter((row) => new Date(row.captured_ts) > start) : rows;
}

/**
 * The widest span listing fewer than DEFAULT_SPAN_MAX_ROWS files — or a week,
 * when even a week lists that many.
 */
function defaultSpan(counts: Record<SpanKey, number>): SpanKey {
  let chosen: SpanKey = 'week';
  SPANS.forEach(({ key }) => {
    if (counts[key] < DEFAULT_SPAN_MAX_ROWS) chosen = key;
  });
  return chosen;
}

export default function EpisodesListView(): ReactElement {
  const [userProfile] = useUser();
  const admin = isAdmin(userProfile);
  const [date, setDate] = useState<string>(todayIsoDate());
  // The span the user picked; null until they pick one, and the default rules.
  const [pickedSpan, setPickedSpan] = useState<SpanKey | null>(null);
  // Admins start where the admin viewer always started: every team's files.
  const [allTeams, setAllTeams] = useState<boolean>(admin);
  const navigate = useNavigate();

  const everyTeam = admin && allTeams;
  // One fetch of every file up to the date; the spans are cut from it here, so
  // each can show its count and switching between them costs no request.
  const end = endOfDay(date);
  const toIso = end.toISOString();
  const adminList = useEpisodeList(undefined, toIso, everyTeam);
  const teamList = useTeamEpisodeFiles(undefined, toIso, !everyTeam);
  const { data, isPending, isError, error, refetch, isFetching } = everyTeam ? adminList : teamList;

  const counts = useMemo(() => {
    const rows: RecordingRow[] = data ?? [];
    const out = {} as Record<SpanKey, number>;
    SPANS.forEach(({ key }) => {
      out[key] = rowsInSpan(rows, key, end).length;
    });
    return out;
  }, [data, toIso]);
  const span = pickedSpan ?? defaultSpan(counts);
  const rows = useMemo(() => rowsInSpan(data ?? [], span, end), [data, span, toIso]);

  const changeDate = (next: string) => {
    setDate(next || todayIsoDate());
    // A new date gets the default span again: its counts are different.
    setPickedSpan(null);
  };

  const columns = useMemo<ColumnDef<RecordingRow, unknown>[]>(
    () => [
      ...(everyTeam
        ? [
          {
            accessorKey: 'cached',
            header: '',
            cell: ({ row }) => (row.original.cached ? '' : '★'),
            size: 24,
          } as ColumnDef<RecordingRow, unknown>,
        ]
        : []),
      {
        accessorKey: 'captured_ts',
        header: 'Captured (UTC)',
        cell: ({ row }) => row.original.captured_ts.replace('T', ' ').slice(0, 19),
      },
      { accessorKey: 'filename', header: 'Filename' },
      { accessorKey: 'device_id', header: 'Device ID' },
    ],
    [everyTeam],
  );

  const handleRowClick = (_e: React.MouseEvent, row: RecordingRow) => {
    navigate(`/account/episodes/detail/${row.s3_key}`);
  };

  return (
    <>
      <SectionHeader
        title="Recordings"
        isLoading={isFetching}
        backLink={{ to: '/account', text: 'Back to account' }}
      />

      <FormCard>
        <div className="cr-flex cr-flex-col cr-gap-3 cr-p-3 md:cr-flex-row md:cr-items-end">
          <label className="cr-flex cr-flex-col">
            <span className="cr-text-xs cr-font-medium cr-text-gray-500">Up to</span>
            <input
              type="date"
              className="cr-mt-1 cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
              value={date}
              onChange={(e) => changeDate(e.target.value)}
            />
          </label>
          <div className="cr-flex cr-flex-col">
            <span className="cr-text-xs cr-font-medium cr-text-gray-500">Going back</span>
            <div className="cr-mt-1 cr-flex cr-overflow-hidden cr-rounded-md cr-border cr-border-gray-300">
              {SPANS.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={span === key}
                  onClick={() => setPickedSpan(key)}
                  className={cn(
                    'cr-whitespace-nowrap cr-border-none cr-px-3 cr-py-2 cr-text-sm',
                    span === key
                      ? 'cr-bg-blue-900 cr-font-semibold cr-text-white'
                      : 'cr-bg-white cr-text-gray-700 hover:cr-bg-gray-50',
                  )}
                >
                  {label}
                  {data ? ` (${counts[key]})` : ''}
                </button>
              ))}
            </div>
          </div>
          <button
            className="cr-rounded-md cr-bg-blue-600 cr-px-3 cr-py-2 cr-text-sm cr-font-medium cr-text-white hover:cr-bg-blue-700"
            onClick={() => refetch()}
          >
            Refresh
          </button>
          {admin && (
            <label className="cr-flex cr-items-center cr-gap-2 cr-whitespace-nowrap cr-pb-2 cr-text-sm cr-text-gray-700">
              <input
                type="checkbox"
                checked={allTeams}
                onChange={(e) => setAllTeams(e.target.checked)}
                className="cr-cursor-pointer"
              />
              All teams
            </label>
          )}
        </div>
        <p className="cr-px-3 cr-pb-2 cr-text-left cr-text-xs cr-text-gray-500">
          {everyTeam
            ? 'Every recording file in the bucket, whichever team it belongs to — and from phones that never ' +
              'registered a recording with any team, which show only here. Open one and link a recording to a ' +
              'session to claim it for the team your account is in. ★ = not yet cached on the server.'
            : 'Recording files from your team’s phones, and files your team’s logins uploaded. ' +
              'Open one to see which of its runs are on the server.'}
        </p>
      </FormCard>

      {isPending && <Spinner isFetching />}

      {isError && (
        <p className="cr-mt-2 cr-text-sm cr-text-red-600">
          Failed to load recordings: {(error as Error)?.message ?? 'unknown error'}
        </p>
      )}

      {!isPending && !isError && (
        rows.length > 0 ? (
          <Table<RecordingRow>
            data={rows}
            columns={columns}
            onRowClick={handleRowClick}
            getRowId={(row) => row.s3_key}
          />
        ) : (
          <p className="cr-mt-2 cr-text-sm cr-text-gray-500">No recordings uploaded in this period.</p>
        )
      )}
    </>
  );
}
